import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Article } from '../models/article.model';
import { ArticleStateModel } from '../models/article.state-model';
import {
    AddArticles,
    CreateArticle,
    CreateArticleFailure,
    CreateArticleSuccess,
    DeleteArticle,
    DeleteArticleFailure,
    DeleteArticleSuccess,
    FetchArticle,
    FetchArticleByUniqueId,
    FetchArticleByUniqueIdFailure,
    FetchArticleByUniqueIdSuccess,
    FetchArticleFailure,
    FetchArticles,
    FetchArticlesAuthors,
    FetchArticlesAuthorsFailure,
    FetchArticlesAuthorsSuccess,
    FetchArticlesFailure,
    FetchArticlesSuccess,
    FetchArticleSuccess,
    SetArticle,
    SetArticles,
    UnsetArticle,
    UpdateArticle,
    UpdateArticleFailure,
    UpdateArticleRelatedPostings,
    UpdateArticleRelatedPostingsFailure,
    UpdateArticleRelatedPostingsSuccess,
    UpdateArticleSuccess,
} from '../actions';
import { Injectable } from '@angular/core';
import { ArticleApiService } from '../services/article.api-service';
import { HttpErrorResponse } from '@angular/common/http';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';

@State<ArticleStateModel>({
    name: 'articles',
    defaults: new ArticleStateModel(),
})
@Injectable()
export class ArticleState extends EntityState<Article> implements IEntityState<Article> {
    @Selector()
    static getEntity(state: ArticleStateModel, id: string = state.item): Article {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntitiesAsMap(state: ArticleStateModel): Map<string, Article> {
        if (!state.map) {
            return null;
        }
        return state.map;
    }

    @Selector()
    static getEntities(state: ArticleStateModel): Article[] {
        if (!state.map) {
            return null;
        }

        return Array.from(state.map.values());
    }

    constructor(private _articleApiService: ArticleApiService) {
        super(_articleApiService, 0);

        this._propstoSearch = Article.props_to_search;
    }

    @Action(AddArticles)
    addEntities(ctx: StateContext<ArticleStateModel>, action: AddArticles): void {
        this._addEntities(ctx, action);
    }

    @Action(CreateArticle)
    createEntity(ctx: StateContext<ArticleStateModel>, action: CreateArticle): void {
        this._createEntity(ctx, action, CreateArticleSuccess, CreateArticleFailure, AddArticles).then(() => {});
    }

    @Action(SetArticle)
    setEntity(ctx: StateContext<ArticleStateModel>, action: SetArticle): void {
        this._setEntity(ctx, action, AddArticles);
    }

    @Action(DeleteArticle)
    deleteEntity(ctx: StateContext<ArticleStateModel>, action: DeleteArticle): void {
        this._deleteEntity(ctx, action, DeleteArticleSuccess, DeleteArticleFailure).then(() => {});
    }

    @Action(FetchArticle)
    fetchEntity(ctx: StateContext<ArticleStateModel>, action: FetchArticle): void {
        this._fetchEntity(ctx, action, FetchArticleSuccess, FetchArticleFailure, AddArticles, SetArticle).then(() => {});
    }

    @Action(FetchArticleByUniqueId)
    fetchEntityByUniqueId(ctx: StateContext<ArticleStateModel>, action: FetchArticleByUniqueId): void {
        this._articleApiService.readByUniqueId(action.payload).subscribe(
            (entity) => {
                this._addEntities(ctx, { payload: [entity] });
                ctx.dispatch(new FetchArticleByUniqueIdSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchArticleByUniqueIdFailure(response));
            }
        );
    }

    @Action(FetchArticlesAuthors)
    fetchArticleAuthors(ctx: StateContext<ArticleStateModel>, action: FetchArticlesAuthors): void {
        this._articleApiService.fetchArticleAuthors().subscribe(
            (entities) => {
                ctx.dispatch(new FetchArticlesAuthorsSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchArticlesAuthorsFailure());
            }
        );
    }

    @Action(FetchArticleSuccess)
    fetchEntitySuccess(ctx: StateContext<ArticleStateModel>, action: FetchArticleSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchArticleFailure)
    fetchEntityFailure(ctx: StateContext<ArticleStateModel>, action: FetchArticleFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(FetchArticles)
    fetchEntities(ctx: StateContext<ArticleStateModel>, SetEntityAction: any, SuccessAction: any, FailureAction: any): void {
        this._fetchEntities(ctx, FetchArticleSuccess, FetchArticleFailure, SetArticle).then(() => {});
    }

    @Action(FetchArticlesFailure)
    fetchEntitiesFailure(ctx: StateContext<ArticleStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }

    @Action(FetchArticlesSuccess)
    fetchEntitiesSuccess(ctx: StateContext<ArticleStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(SetArticles)
    setEntities(ctx: StateContext<ArticleStateModel>, action: SetArticles): void {
        this._setEntities(ctx, action);
    }

    @Action(UnsetArticle)
    unsetEntity(ctx: StateContext<ArticleStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(UpdateArticle)
    updateEntity(ctx: StateContext<ArticleStateModel>, action: UpdateArticle): void {
        this._updateEntity(ctx, action, UpdateArticleSuccess, UpdateArticleFailure, AddArticles).then(() => {});
    }

    @Action(UpdateArticleRelatedPostings)
    updateArticleRelatedPostings(ctx: StateContext<ArticleStateModel>, { payload }: UpdateArticleRelatedPostings): void {
        this._articleApiService.updateArticleRelatedPostings(payload.articleId, payload.postings).subscribe(
            (entity) => {
                this._addEntities(ctx, { payload: [entity] });
                ctx.dispatch(new UpdateArticleRelatedPostingsSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new UpdateArticleRelatedPostingsFailure(response));
            }
        );
    }
}
